export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-2 text-3xl font-bold">Founding Maker Terms</h1>
        <p className="mb-1 text-sm text-muted-foreground">Effective date: July 29, 2026</p>
        <p className="mb-8 text-sm text-muted-foreground">Version 1.3</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground dark:prose-invert">
          <section>
            <p>
              These terms explain how selling on Stoop works, what you get as a Founding Maker, and what each of us is
              responsible for. We have written them in plain English on purpose, but they are a binding legal agreement.
              Please read them before you list.
            </p>
            <p>
              Stoop is operated by <strong>MarketLink Labs LLC</strong>, a New York limited liability company doing
              business as &ldquo;Stoop&rdquo; (&ldquo;Stoop&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
              &ldquo;our&rdquo;). You, the person or business listing products, are the &ldquo;Maker&rdquo; or
              &ldquo;you&rdquo;.
            </p>
            <p>
              These Founding Maker Terms supplement, and are in addition to, our general{" "}
              <a href="/terms-of-use" className="underline">
                Terms of Use
              </a>
              , which apply to everyone who uses Stoop. By creating a seller account, agreeing to these terms at
              onboarding, or publishing a listing, you agree to these terms, the Terms of Use, our{" "}
              <a href="/privacy" className="underline">
                Privacy Policy
              </a>
              , and the seller compliance attestation you complete before publishing. If you do not agree, do not sell
              on Stoop.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">1. What Stoop is (and is not)</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Stoop is an online local marketplace. We provide a storefront, tools to list products, discovery for
                nearby buyers, and payment processing through our third-party processor.
              </li>
              <li>
                <strong>You are an independent seller.</strong> You are not our employee, partner, agent, or joint
                venturer. You run your own business, set your own terms, and control your own products. Nothing in these
                terms creates any relationship other than that of independent contracting parties.
              </li>
              <li>
                <strong>Stoop is a venue, not the seller of your goods.</strong> Each sale is a direct contract between
                you and your buyer. We are not the manufacturer, producer, distributor, or merchant of record for your
                products, and we do not take title to them or take possession of them.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">2. Eligibility and your account</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You must be at least 18 years old and able to enter a binding contract.</li>
              <li>You must provide accurate account, business, and payout information and keep it current.</li>
              <li>
                You are responsible for all activity under your account and for keeping your login credentials secure.
                Notify us promptly of any unauthorized use.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">3. Your Founding Maker status</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Founding Maker status is offered to the first 50 makers to publish a listing on Stoop.</li>
              <li>
                <strong>Your status locks in when you publish your first product.</strong> Until then it is reserved,
                not final.
              </li>
              <li>
                Founding perks: <strong>0% seller fee for your first 6 months</strong> (measured from your first
                published listing), a permanent <strong>Founding Maker badge</strong>, and additional early-maker perks
                we may offer to help you grow (subject to eligibility and program rules, which may change as the network
                grows).
              </li>
              <li>
                We may modify, suspend, or end the Founding program at any time. A perk you have already locked in, such
                as a 0% fee window that has already started, will be honored for its stated duration.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">4. Fees and what you keep</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>During your 6-month Founding window: 0% seller fee.</strong> You keep 100% of your listed item
                price. Stoop also absorbs the payment processor&rsquo;s transaction fees during this window, so they do
                not reduce your payout.
              </li>
              <li>
                <strong>After the Founding window: a 1% seller fee</strong> on your item subtotal. For example, on a $20
                order the seller fee is about $0.20, so you keep roughly $19.80.
              </li>
              <li>
                <strong>Buyers pay a separate service fee to Stoop</strong>, calculated as a percentage of their order
                that decreases on larger orders. That fee is charged to the buyer and does not reduce your payout.
              </li>
              <li>
                We will give you advance notice before increasing your seller fee. The fee details shown in the app at
                the time of a sale control if they differ from the examples here.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">5. Payments, payouts, and holds</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Payments and payouts are handled by <strong>Stripe</strong> (our payment processor). To be paid, you
                will create and connect a Stripe account and complete Stripe&rsquo;s identity and bank verification. Your
                use of Stripe is also governed by the Stripe Connected Account Agreement and Stripe&rsquo;s other terms.
              </li>
              <li>
                Buyers pay through the app. Your earnings, net of any applicable fees, refunds, chargebacks, taxes we are
                required to collect, and adjustments, are paid to your connected account on Stripe&rsquo;s standard
                payout schedule.
              </li>
              <li>
                <strong>Holds and offsets.</strong> We (through Stripe) may place a reasonable hold or reserve on, delay,
                or offset a payout if we reasonably suspect fraud, an unusually high rate of refunds or chargebacks, a
                legal or payment-network requirement, or a breach of these terms, while we investigate. If we pay you an
                amount in error, or a buyer&rsquo;s payment is later reversed, you authorize us to recover or offset that
                amount from your balance or future payouts.
              </li>
              <li>Stoop does not store your full card or bank numbers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">6. You are in control</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You set your own prices, product descriptions, photos, and availability.</li>
              <li>You set your own lead times, deposit amounts, and custom-order rules for made-to-order work.</li>
              <li>You choose fulfillment: pickup from you, meeting at a market, or delivery where available.</li>
              <li>You may decline or cancel any order you cannot or choose not to fulfill.</li>
              <li>
                <strong>No exclusivity.</strong> You are free to sell through any other channel at the same time. Stoop
                never requires that it be your only sales channel.
              </li>
              <li>
                <strong>You are never locked in.</strong> You may stop listing or close your account at any time (see
                Section 18).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">7. Your responsibilities, product safety, and insurance</h2>
            <p>You are solely responsible for your products and your business, including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Food safety, cottage-food, labeling, and licensing compliance.</strong> You are responsible for
                meeting every food-safety, cottage-food, home-processing, labeling, allergen, weights-and-measures, and
                licensing requirement that applies to what you make and sell. <strong>Stoop is not the authority on these
                laws, does not provide legal advice, and does not verify your compliance.</strong> We surface links to
                official government resources as a convenience only. Before publishing, you will complete and stand
                behind a compliance attestation.
              </li>
              <li>
                <strong>Product safety and liability.</strong> You are solely responsible for the safety, quality, and
                legality of your products and for any injury, illness, allergic reaction, property damage, or other harm
                they cause. <strong>You are strongly encouraged to carry your own product-liability and general-liability
                insurance. Stoop does not insure you, your products, or your customers.</strong>
              </li>
              <li>
                <strong>Accurate, honest listings</strong>, including ingredients, allergens, sourcing claims, and
                anything a reasonable buyer would need to know.
              </li>
              <li>
                <strong>Fulfilling the orders you accept</strong> on the terms and timing you set, handling your own
                customer service, refunds, and recalls, and communicating with your buyers.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">8. Your representations and warranties</h2>
            <p>Each time you list or sell, you represent and warrant that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>you have the legal right and authority to enter these terms and to sell your products;</li>
              <li>
                you and your products comply with all applicable laws, including food-safety, cottage-food, licensing,
                labeling, and sales-tax-registration requirements;
              </li>
              <li>your listings are accurate and not misleading;</li>
              <li>
                you own, or have the necessary rights and licenses to, all content you upload, and it does not infringe
                or violate anyone else&rsquo;s rights;
              </li>
              <li>you will fulfill the orders you accept; and</li>
              <li>you are not barred from selling under any applicable law, sanctions, or payment-network rule.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">9. Taxes</h2>
            <p>Taxes are the part sellers ask about most, so here is the honest, complete picture.</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Income reporting (1099-K).</strong> Our payment processor (Stripe) reports your gross sales to
                the IRS and issues you a 1099-K once you cross the reporting threshold (currently $5,000 in a calendar
                year, subject to change by the IRS). Your Stoop earnings are self-employment income that you are
                responsible for reporting. We do not withhold taxes for you.
              </li>
              <li>
                <strong>Expense tracking.</strong> Stoop includes an expense-tracking tool so you can record business
                costs in one place. It is a convenience, not tax advice or a filing service.
              </li>
              <li>
                <strong>Sales tax — important.</strong> A 1099-K is <em>income</em> reporting; it is <strong>not</strong>{" "}
                sales tax. Stripe processes payments and issues the 1099-K, but it does not automatically calculate,
                collect, file, or remit sales tax for your sales on Stoop by default. Except as described in the
                marketplace-facilitator paragraph below, registering for, collecting, and remitting any sales tax you owe
                is your responsibility. Most unprepared food sold for home consumption is exempt in New York, but many
                prepared foods and non-food items (for example candles, soaps, crafts, apparel, and printed goods) are
                taxable. Check your state&rsquo;s rules and register before your first taxable sale.
              </li>
              <li>
                <strong>Marketplace facilitator laws.</strong> Some states require an online marketplace to collect and
                remit sales tax on behalf of its third-party sellers once the marketplace meets certain thresholds. Where
                Stoop is legally required to act as a marketplace facilitator for a given sale, Stoop (not you) will
                collect and remit the sales tax on that sale, and we will tell you when that applies to your listings.
                Outside of those cases, sales tax remains your responsibility as described above.
              </li>
              <li>
                Nothing in this section is tax or legal advice. Consult a qualified tax professional about your
                situation.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">10. Refunds, cancellations, and disputes with buyers</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                You set and communicate your own refund and cancellation policy, consistent with applicable
                consumer-protection law.
              </li>
              <li>
                Refunds and payment disputes are processed through Stripe. Refunded amounts, chargebacks, and related
                processing costs may be deducted from or charged back against your payouts.
              </li>
              <li>
                We may, but are not obligated to, help mediate a dispute between you and a buyer. The underlying product
                or service issue remains between you and your buyer.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">11. Your content, intellectual property, and feedback</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>You keep ownership of your content</strong> — your photos, descriptions, logo, and brand remain
                yours.
              </li>
              <li>
                <strong>License to Stoop.</strong> You grant Stoop a non-exclusive, royalty-free, worldwide license to
                host, store, display, reproduce, and promote your content in connection with operating and marketing the
                platform (for example, featuring your storefront in a Stoop post). You may ask us to stop featuring you in
                marketing at any time; the license otherwise lasts while your content is on Stoop and for a reasonable
                wind-down period after.
              </li>
              <li>
                <strong>Feedback.</strong> If you send us ideas, suggestions, or feedback about Stoop, you grant us a
                perpetual, irrevocable, royalty-free, worldwide license to use them for any purpose without any
                obligation, attribution, or compensation to you.
              </li>
              <li>
                <strong>Infringement claims.</strong> We respect intellectual property. If you believe content on Stoop
                infringes your rights, email{" "}
                <a href="mailto:hello@trystoop.com" className="underline">
                  hello@trystoop.com
                </a>{" "}
                with details, and we will review and remove infringing material where appropriate. We may remove content
                and terminate repeat infringers.
              </li>
              <li>
                <strong>No duty to monitor.</strong> We may, but have no obligation to, monitor, review, or edit content,
                and we are not responsible for content posted by users.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">12. Privacy and data</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Our handling of personal information is described in our{" "}
                <a href="/privacy" className="underline">
                  Privacy Policy
                </a>
                , which is incorporated into these terms.
              </li>
              <li>
                <strong>Home addresses are protected.</strong> For home-based makers, we display only an approximate area
                publicly, not your exact home location. Exact pickup details are shared with a buyer only as needed to
                complete an order.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">13. Acceptable use and prohibited items</h2>
            <p>You agree not to, and not to list or sell:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>anything illegal, unsafe, recalled, spoiled, counterfeit, stolen, or infringing;</li>
              <li>
                age-restricted or regulated products — including alcohol, tobacco or vaping products, cannabis or CBD,
                firearms or weapons, and drugs — unless you hold every required license, you comply with all applicable
                laws, and Stoop permits the category in writing;
              </li>
              <li>
                foods prohibited or restricted under applicable cottage-food or home-processing rules (for example,
                certain low-acid canned goods, unpasteurized or raw dairy where restricted, and meat or poultry not
                produced and processed in compliance with applicable law, including any inspection, facility, or
                exemption requirements that apply to you);
              </li>
              <li>
                anything you are not authorized to sell, or that misrepresents your products, identity, licensing, or
                compliance.
              </li>
            </ul>
            <p>
              You also agree not to defraud buyers, harass others, scrape or overload the platform, or attempt to break
              or circumvent its security or these terms. We may remove listings and suspend or terminate accounts that
              violate this section or the law, and we have no obligation to pre-screen listings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">14. Disclaimer of warranties</h2>
            <p>
              Stoop is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; and may include beta or
              still-developing features that can change or break. To the fullest extent permitted by law, we disclaim all
              warranties, express or implied, including merchantability, fitness for a particular purpose, title, and
              non-infringement. We do not warrant that the platform will be uninterrupted, error-free, or secure, or that
              any level of sales, visibility, or income will result from listing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">15. Limitation of liability</h2>
            <p>To the fullest extent permitted by law:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Stoop is not liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or
                for lost profits, revenue, data, or goodwill, even if advised of the possibility.
              </li>
              <li>
                <strong>Stoop&rsquo;s total aggregate liability</strong> for any and all claims arising out of or relating
                to these terms or your use of the platform will not exceed the greater of (a) the total seller fees you
                paid to Stoop in the 6 months before the event giving rise to the claim, or (b) $100.
              </li>
              <li>
                These limits apply regardless of the theory of liability and even if a remedy fails of its essential
                purpose. They do not apply to liability that cannot be limited under applicable law.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">16. Indemnification</h2>
            <p>
              You will defend, indemnify, and hold harmless Stoop and its operator, owners, successors, and assigns from
              and against any claims, damages, losses, liabilities, and reasonable costs and attorneys&rsquo; fees arising
              out of or relating to: (a) the products you make, list, or sell (including their safety, quality, labeling,
              allergens, and any recall); (b) your violation of these terms or any law; (c) your taxes; (d) your content;
              or (e) your dispute with a buyer or any third party. We may assume the exclusive defense of any matter
              subject to indemnification, at your expense, and you will cooperate. This section survives termination.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">17. Dispute resolution, governing law, and waivers</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Governing law.</strong> These terms are governed by the laws of the State of New York, without
                regard to its conflict-of-laws rules.
              </li>
              <li>
                <strong>Talk to us first.</strong> Before starting a formal proceeding, you agree to email{" "}
                <a href="mailto:hello@trystoop.com" className="underline">
                  hello@trystoop.com
                </a>{" "}
                and try in good faith to resolve the dispute informally for at least 30 days.
              </li>
              <li>
                <strong>Venue.</strong> If informal resolution fails, the dispute must be brought exclusively in the state
                courts located in Rensselaer County, New York, or in the U.S. District Court for the Northern District of
                New York, and you and Stoop each consent to that jurisdiction and venue. Either party may still bring a
                qualifying claim in small claims court.
              </li>
              <li>
                <strong>Jury-trial waiver.</strong> To the extent permitted by law, you and Stoop each waive any right to
                a jury trial.
              </li>
              <li>
                <strong>Class-action waiver.</strong> To the extent permitted by law, disputes must be brought
                individually, and not as a plaintiff or class member in any class, collective, or representative
                proceeding.
              </li>
              <li>
                <strong>Time limit.</strong> To the extent permitted by law, any claim must be filed within one year after
                it arises, or it is permanently barred.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">18. Term and termination</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>You may leave at any time</strong> by closing your account or ceasing to list. You should still
                honor any orders already in progress.
              </li>
              <li>
                We may suspend or terminate your account or access for violation of these terms, legal or safety risk, or
                if required by law. Where practical, we will give you notice.
              </li>
              <li>
                On termination, we will pay out amounts properly owed to you (net of refunds, chargebacks, taxes, and
                adjustments) on the normal schedule. Any provision that by its nature should survive termination —
                including those on fees and amounts owed, payments, your responsibilities and product liability, your
                representations, taxes, content and intellectual property, disclaimers, limitation of liability,
                indemnification, dispute resolution, and general provisions — survives.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">19. Assignment</h2>
            <p>
              You may not assign or transfer these terms without our prior written consent. <strong>We may assign or
              transfer these terms</strong>, in whole or in part, to any successor, affiliate, or acquirer, without
              further consent from you. Upon such assignment, the
              assignee assumes our role as operator of Stoop and these terms continue in force unchanged.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">20. General provisions</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Changes.</strong> We may update these terms as Stoop grows. If a change materially affects you
                (such as your fee), we will notify you before it takes effect. Continued use after a change takes effect
                means you accept the updated terms.
              </li>
              <li>
                <strong>Entire agreement.</strong> These terms, the Privacy Policy, and your compliance attestation are
                the entire agreement between you and Stoop about selling on the platform, and supersede all prior
                discussions, representations, and informal messages.
              </li>
              <li>
                <strong>Severability and reformation.</strong> If any provision is held unenforceable, it will be modified
                to the minimum extent necessary to be enforceable (or severed if it cannot be), and the rest stays in full
                effect.
              </li>
              <li>
                <strong>No waiver.</strong> Our failure to enforce a provision is not a waiver of it.
              </li>
              <li>
                <strong>Force majeure.</strong> Neither party is liable for delays or failures caused by events beyond its
                reasonable control.
              </li>
              <li>
                <strong>Relationship.</strong> The parties are independent contractors; these terms create no agency,
                partnership, employment, or joint venture.
              </li>
              <li>
                <strong>Headings.</strong> Headings are for convenience only and do not affect interpretation.
              </li>
              <li>
                <strong>Notices and electronic records.</strong> We may give you notice in the app or by email to the
                address on your account; notices to us go to hello@trystoop.com. You consent to receive records and
                agreements electronically, and you agree that your electronic acceptance — creating a seller account,
                agreeing at onboarding, or publishing a listing — has the same effect as a handwritten signature.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">21. Contact</h2>
            <p>
              Questions before you list? Reach out any time — in-app support or{" "}
              <a href="mailto:hello@trystoop.com" className="underline">
                hello@trystoop.com
              </a>
              . We would rather answer now than have you wonder later.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
